import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, GitCommit, Sparkles, Plus, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TRADE_LABEL = { scalp: "Scalp", daytrade: "Daytrade", swingtrade: "Swingtrade", hodl: "Hodl" };

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("de-DE", { day: "2-digit", month: "short" }) : "";

/**
 * „Deine Strategie-Entwicklung" — Übersichts-Karte pro aktiver Strategie mit
 * Mini-Timeline (V1 · V2 · … · Aktuell), letzter Nutzer-Notiz und Direktsprung
 * in die Detailseite.
 */
export default function StrategyEvolutionCard() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/strategies`, { withCredentials: true });
      setStrategies(res.data || []);
    } catch {
      setStrategies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(
    () => strategies.filter((s) => s.status === "aktiv").slice(0, 4),
    [strategies]
  );

  return (
    <div className="card-surface p-6" data-testid="strategy-evolution-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2">
            <GitCommit className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />
            Deine Strategie-Entwicklung
          </div>
          <h2 className="font-heading text-xl mt-1">Wie sich deine Regeln entwickeln</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Jede Aktivierung und jede Inhaltsänderung erzeugt einen Snapshot. Hier siehst du, was du zuletzt an deinen aktiven Strategien angepasst hast.
          </p>
        </div>
        <Link to="/app/strategien">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full h-9 px-3"
            data-testid="evolution-all-strategies"
          >
            Alle Strategien <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-32 rounded-xl border border-border bg-secondary/30 animate-pulse" />
          ))}
        </div>
      ) : active.length === 0 ? (
        <EmptyState hasAny={strategies.length > 0} />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {active.map((s) => (
            <li key={s.id}>
              <StrategyRow strategy={s} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StrategyRow({ strategy }) {
  const versions = strategy.versions || [];
  const total = versions.length + 1; // +1 = current
  // Bis zu 5 Dots anzeigen (V1..V4 + Aktuell). Bei mehr: „…" davor.
  const shownVersions = versions.slice(-4);
  const hidden = versions.length - shownVersions.length;
  const latest = versions.length ? versions[versions.length - 1] : null;
  const latestNote = latest?.user_note;
  const tradeTypesLabel = (strategy.trade_types || []).map((t) => TRADE_LABEL[t] || t).join(" · ") || "—";

  return (
    <Link
      to={`/app/strategien/${strategy.id}`}
      className="block rounded-xl border border-border bg-secondary/40 hover:bg-secondary/70 transition-colors p-4 h-full group"
      data-testid={`evolution-row-${strategy.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading text-base truncate">{strategy.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{tradeTypesLabel}</div>
        </div>
        <Badge
          variant="secondary"
          className="rounded-full text-[10px] font-mono bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]"
        >
          {total}× V
        </Badge>
      </div>

      <div className="flex items-center gap-1.5 mt-4" aria-label="Versions-Timeline">
        {hidden > 0 && (
          <span className="text-[10px] text-muted-foreground font-mono mr-0.5">+{hidden}</span>
        )}
        {shownVersions.map((v) => (
          <Dot key={v.version_no} label={`V${v.version_no}`} hasNote={!!v.user_note} />
        ))}
        <Chevron />
        <Dot label="Aktuell" isCurrent />
      </div>

      {latestNote ? (
        <div className="mt-3 text-sm text-foreground italic line-clamp-2">
          „{latestNote}"
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5 not-italic">
            V{latest.version_no} · {fmtDate(latest.saved_at)}
          </div>
        </div>
      ) : latest ? (
        <div className="mt-3 text-xs text-muted-foreground">
          Letzte Änderung: {latest.change_note} · {fmtDate(latest.saved_at)}
        </div>
      ) : (
        <div className="mt-3 text-xs text-muted-foreground italic">
          Noch keine Version — bearbeite die Strategie einmal, um den Verlauf zu starten.
        </div>
      )}

      <div className="mt-3 text-xs text-primary-foreground/80 group-hover:text-primary-foreground inline-flex items-center gap-1">
        Details öffnen <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
      </div>
    </Link>
  );
}

function Dot({ label, isCurrent = false, hasNote = false }) {
  return (
    <div className="flex flex-col items-center">
      <span
        className={`h-2 w-2 rounded-full ${
          isCurrent
            ? "bg-primary ring-2 ring-primary/30"
            : hasNote
            ? "bg-[hsl(32_60%_62%)]"
            : "bg-muted-foreground/60"
        }`}
        title={hasNote ? "Enthält Notiz" : label}
      />
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1 font-mono">
        {label}
      </span>
    </div>
  );
}

function Chevron() {
  return (
    <div className="h-px w-4 bg-border mx-0.5" aria-hidden="true" />
  );
}

function EmptyState({ hasAny }) {
  return (
    <div
      className="rounded-xl border border-dashed border-border p-6 text-center"
      data-testid="evolution-empty"
    >
      <Sparkles className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
      <div className="font-heading text-base mt-3">
        {hasAny ? "Noch keine aktive Strategie" : "Deine erste Strategie startet den Verlauf"}
      </div>
      <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
        {hasAny
          ? "Du hast Entwürfe, aber noch keine aktivierte Strategie. Öffne einen Entwurf und aktiviere ihn, um deinen Versionsverlauf zu starten."
          : "Sobald du eine Strategie mit dem KI-Coach entwickelt und aktiviert hast, entsteht hier automatisch eine Timeline deiner Änderungen."}
      </p>
      <Link to="/app/strategien" className="inline-block mt-4">
        <Button variant="outline" className="rounded-full" data-testid="evolution-cta">
          <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
          {hasAny ? "Zu deinen Entwürfen" : "Erste Strategie anlegen"}
        </Button>
      </Link>
    </div>
  );
}
